import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { getReports, takeReportAction, getAuditLog, getSanctions, createSanction, revokeSanction } from '../lib/api';
import { Card } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Skeleton } from '../components/ui/skeleton';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Textarea } from '../components/ui/textarea';
import { Input } from '../components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { toast } from 'sonner';
import { Link } from 'react-router-dom';
import { formatDate, ROLE_LABELS } from '../lib/utils';
import {
  Shield, AlertTriangle, CheckCircle, XCircle, Lock, MessageSquare, Trash2,
  Eye, ArrowLeft, FileText, Clock, UserX, Ban, AlertOctagon
} from 'lucide-react';

export default function ModerationPage() {
  const { user } = useAuth();
  const [reports, setReports] = useState([]);
  const [auditLogs, setAuditLogs] = useState([]);
  const [sanctions, setSanctions] = useState([]);
  const [counts, setCounts] = useState({});
  const [statusFilter, setStatusFilter] = useState('pending');
  const [loading, setLoading] = useState(true);
  const [actionNote, setActionNote] = useState('');
  const [processingId, setProcessingId] = useState(null);
  const [showSanctionDialog, setShowSanctionDialog] = useState(false);
  const [sanctionForm, setSanctionForm] = useState({ user_id: '', type: 'warn', reason: '', duration: '24h' });

  useEffect(() => {
    loadReports();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusFilter]);

  useEffect(() => {
    loadAuditLog();
    loadSanctions();
  }, []);

  const loadReports = async () => {
    setLoading(true);
    try {
      const res = await getReports({ status: statusFilter, limit: 50 });
      setReports(res.data.reports || []);
      setCounts(res.data.counts || {});
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const loadAuditLog = async () => {
    try {
      const res = await getAuditLog({ limit: 50 });
      setAuditLogs(res.data.logs || []);
    } catch (err) {
      console.error(err);
    }
  };

  const loadSanctions = async () => {
    try {
      const res = await getSanctions({ active_only: false, limit: 50 });
      setSanctions(res.data.sanctions || []);
    } catch (err) {
      console.error(err);
    }
  };

  const handleAction = async (reportId, action) => {
    setProcessingId(reportId);
    try {
      await takeReportAction(reportId, { action, note: actionNote });
      toast.success(`Action "${action}" appliquée`);
      setActionNote('');
      loadReports();
      loadAuditLog();
    } catch (err) {
      toast.error('Erreur lors de l\'action');
    } finally {
      setProcessingId(null);
    }
  };

  const handleCreateSanction = async () => {
    if (!sanctionForm.user_id || !sanctionForm.reason) {
      toast.error('ID utilisateur et raison requis');
      return;
    }
    try {
      await createSanction(sanctionForm);
      toast.success('Sanction appliquée');
      setShowSanctionDialog(false);
      setSanctionForm({ user_id: '', type: 'warn', reason: '', duration: '24h' });
      loadSanctions();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur');
    }
  };

  const handleRevokeSanction = async (sanctionId) => {
    try {
      await revokeSanction(sanctionId);
      toast.success('Sanction révoquée');
      loadSanctions();
    } catch (err) {
      toast.error('Erreur');
    }
  };

  if (!user || (user.role !== 'admin' && user.role !== 'mod')) {
    return (
      <div className="mx-auto max-w-4xl px-3 sm:px-4 lg:px-6 py-12 text-center">
        <Shield className="w-16 h-16 mx-auto text-muted-foreground mb-4" />
        <h2 className="font-heading text-2xl font-bold mb-2">Accès restreint</h2>
        <p className="text-muted-foreground">Connectez-vous avec un compte admin.</p>
      </div>
    );
  }

  const statusBadge = (status) => {
    const styles = {
      pending: 'bg-amber-100 text-amber-800',
      reviewed: 'bg-blue-100 text-blue-800',
      resolved: 'bg-green-100 text-green-800',
      dismissed: 'bg-gray-100 text-gray-800'
    };
    const labels = { pending: 'En attente', reviewed: 'Examiné', resolved: 'Résolu', dismissed: 'Rejeté' };
    return <Badge className={styles[status] || ''}>{labels[status] || status}</Badge>;
  };

  return (
    <div className="mx-auto max-w-6xl px-3 sm:px-4 lg:px-6 py-4 sm:py-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <Link to="/admin" className="flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground mb-2 transition-colors duration-150">
            <ArrowLeft className="w-4 h-4" /> Dashboard
          </Link>
          <h1 className="font-heading text-3xl font-bold flex items-center gap-3">
            <Shield className="w-8 h-8 text-[hsl(var(--primary))]" />
            File de modération
          </h1>
        </div>
        <div className="flex gap-2">
          {Object.entries(counts).map(([status, count]) => (
            <Badge key={status} variant="outline" className="text-xs">
              {status === 'pending' ? 'En attente' : status === 'resolved' ? 'Résolu' : status === 'dismissed' ? 'Rejeté' : status}: {count}
            </Badge>
          ))}
        </div>
      </div>

      <Tabs defaultValue="reports">
        <TabsList className="mb-4">
          <TabsTrigger value="reports" className="gap-1.5">
            <AlertTriangle className="w-4 h-4" /> Signalements ({counts.pending || 0})
          </TabsTrigger>
          <TabsTrigger value="sanctions" className="gap-1.5">
            <Ban className="w-4 h-4" /> Sanctions ({sanctions.filter(s => s.is_active).length})
          </TabsTrigger>
          <TabsTrigger value="audit" className="gap-1.5">
            <Clock className="w-4 h-4" /> Journal d'audit
          </TabsTrigger>
        </TabsList>

        <TabsContent value="reports">
          {/* Filter */}
          <div className="flex items-center gap-2 mb-4">
            <span className="text-sm">Statut:</span>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-40">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="pending">En attente</SelectItem>
                <SelectItem value="reviewed">Examiné</SelectItem>
                <SelectItem value="resolved">Résolu</SelectItem>
                <SelectItem value="dismissed">Rejeté</SelectItem>
                <SelectItem value="all">Tous</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Reports List */}
          <div className="space-y-3">
            {loading ? (
              Array.from({length: 3}).map((_,i) => <Skeleton key={i} className="h-32" />)
            ) : reports.length === 0 ? (
              <Card className="p-8 text-center">
                <CheckCircle className="w-12 h-12 mx-auto text-green-500 mb-3" />
                <h3 className="font-heading font-semibold">Aucun signalement en attente</h3>
                <p className="text-sm text-muted-foreground">Tout est en ordre !</p>
              </Card>
            ) : (
              reports.map(report => (
                <Card key={report._id} className="p-4" data-testid="report-item">
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1 flex-wrap">
                        {statusBadge(report.status)}
                        <Badge variant="outline" className="text-[10px]">
                          {report.target_type === 'post' ? <FileText className="w-3 h-3 mr-1" /> : <MessageSquare className="w-3 h-3 mr-1" />}
                          {report.target_type}
                        </Badge>
                        <Badge variant="outline" className="text-[10px]">{report.category}</Badge>
                        <span className="text-xs text-muted-foreground">s@{report.community_slug}</span>
                      </div>
                      <p className="text-sm mb-1">
                        <strong>Signalé par:</strong> {report.reporter_name} • <strong>Auteur:</strong> {report.target_author}
                      </p>
                      <p className="text-xs text-muted-foreground mb-1">
                        <strong>Raison:</strong> {report.reason}
                      </p>
                      <p className="text-xs text-muted-foreground bg-[hsl(var(--muted))] p-2 rounded">
                        {report.target_preview}
                      </p>
                      <p className="text-[10px] text-muted-foreground mt-1">{formatDate(report.created_at)}</p>
                    </div>
                    {report.status === 'pending' && (
                      <div className="flex flex-col gap-1 shrink-0">
                        <Button
                          size="sm" variant="destructive"
                          onClick={() => handleAction(report._id, 'remove')}
                          disabled={processingId === report._id}
                          className="gap-1 text-xs"
                        >
                          <Trash2 className="w-3 h-3" /> Supprimer
                        </Button>
                        <Button
                          size="sm" variant="outline"
                          onClick={() => handleAction(report._id, 'lock')}
                          disabled={processingId === report._id}
                          className="gap-1 text-xs"
                        >
                          <Lock className="w-3 h-3" /> Verrouiller
                        </Button>
                        <Button
                          size="sm" variant="ghost"
                          onClick={() => handleAction(report._id, 'approve')}
                          disabled={processingId === report._id}
                          className="gap-1 text-xs"
                        >
                          <CheckCircle className="w-3 h-3" /> Approuver
                        </Button>
                        <Button
                          size="sm" variant="ghost"
                          onClick={() => handleAction(report._id, 'dismiss')}
                          disabled={processingId === report._id}
                          className="gap-1 text-xs text-muted-foreground"
                        >
                          <XCircle className="w-3 h-3" /> Rejeter
                        </Button>
                      </div>
                    )}
                  </div>
                  {report.action_taken && (
                    <div className="mt-2 p-2 rounded bg-[hsl(var(--muted))]/50 text-xs">
                      <strong>Action:</strong> {report.action_taken} par {report.reviewed_by} • {formatDate(report.reviewed_at)}
                    </div>
                  )}
                </Card>
              ))
            )}
          </div>
        </TabsContent>

        <TabsContent value="sanctions">
          <div className="space-y-4">
            <div className="flex justify-between items-center">
              <h3 className="font-heading font-semibold">Sanctions utilisateurs</h3>
              <Button size="sm" onClick={() => setShowSanctionDialog(true)} className="gap-1.5" data-testid="create-sanction-button">
                <AlertOctagon className="w-4 h-4" /> Nouvelle sanction
              </Button>
            </div>

            <div className="space-y-2">
              {sanctions.length === 0 ? (
                <Card className="p-6 text-center">
                  <CheckCircle className="w-8 h-8 mx-auto text-green-500 mb-2" />
                  <p className="text-sm text-muted-foreground">Aucune sanction</p>
                </Card>
              ) : (
                sanctions.map(sanction => (
                  <Card key={sanction._id} className="p-3" data-testid="sanction-item">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className={`w-2 h-2 rounded-full ${sanction.is_active ? 'bg-red-500' : 'bg-gray-400'}`} />
                        <div>
                          <div className="flex items-center gap-2">
                            <span className="text-sm font-medium">{sanction.username}</span>
                            <Badge className={`text-[10px] ${
                              sanction.type === 'ban' ? 'bg-red-100 text-red-800' :
                              sanction.type === 'timeout' ? 'bg-amber-100 text-amber-800' :
                              'bg-blue-100 text-blue-800'
                            }`}>
                              {sanction.type === 'warn' ? 'Avertissement' : sanction.type === 'timeout' ? `Suspension ${sanction.duration || ''}` : 'Banni'}
                            </Badge>
                            {sanction.is_active && <Badge variant="outline" className="text-[10px] text-red-600">Actif</Badge>}
                          </div>
                          <p className="text-xs text-muted-foreground">{sanction.reason}</p>
                          <p className="text-[10px] text-muted-foreground">
                            Par {sanction.issued_by} • {formatDate(sanction.created_at)}
                            {sanction.expires_at && ` • Expire: ${formatDate(sanction.expires_at)}`}
                          </p>
                        </div>
                      </div>
                      {sanction.is_active && sanction.type !== 'warn' && (
                        <Button size="sm" variant="outline" onClick={() => handleRevokeSanction(sanction._id)} className="text-xs">
                          Révoquer
                        </Button>
                      )}
                    </div>
                  </Card>
                ))
              )}
            </div>
          </div>
        </TabsContent>

        <TabsContent value="audit">
          <Card className="p-4">
            <h3 className="font-heading font-semibold text-sm mb-3">Journal d'audit</h3>
            <div className="space-y-2 max-h-[500px] overflow-y-auto">
              {auditLogs.map((log, i) => (
                <div key={i} className="flex items-center gap-3 p-2 rounded-lg border text-xs">
                  <div className={`w-2 h-2 rounded-full ${
                    log.action === 'approve' ? 'bg-green-500' :
                    log.action === 'remove' ? 'bg-red-500' :
                    log.action === 'lock' ? 'bg-amber-500' : 'bg-gray-400'
                  }`} />
                  <span className="font-medium">{log.moderator || 'AI-Moderator'}</span>
                  <Badge variant="outline" className="text-[10px]">{log.action}</Badge>
                  <span className="text-muted-foreground">{log.target_type}</span>
                  {log.reason && <span className="text-muted-foreground truncate max-w-[200px]">{log.reason}</span>}
                  <span className="text-muted-foreground ml-auto shrink-0">{formatDate(log.created_at)}</span>
                </div>
              ))}
              {auditLogs.length === 0 && (
                <p className="text-sm text-muted-foreground text-center py-4">Aucune entrée</p>
              )}
            </div>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Sanction Creation Dialog */}
      <Dialog open={showSanctionDialog} onOpenChange={setShowSanctionDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading">Nouvelle sanction</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <label className="text-sm font-medium mb-1 block">ID utilisateur</label>
              <Input
                value={sanctionForm.user_id}
                onChange={(e) => setSanctionForm({...sanctionForm, user_id: e.target.value})}
                placeholder="ID de l'utilisateur à sanctionner"
                data-testid="sanction-user-id"
              />
            </div>
            <div>
              <label className="text-sm font-medium mb-1 block">Type</label>
              <Select value={sanctionForm.type} onValueChange={(v) => setSanctionForm({...sanctionForm, type: v})}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="warn">Avertissement</SelectItem>
                  <SelectItem value="timeout">Suspension temporaire</SelectItem>
                  <SelectItem value="ban">Bannissement permanent</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {sanctionForm.type === 'timeout' && (
              <div>
                <label className="text-sm font-medium mb-1 block">Durée</label>
                <Select value={sanctionForm.duration} onValueChange={(v) => setSanctionForm({...sanctionForm, duration: v})}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="1h">1 heure</SelectItem>
                    <SelectItem value="6h">6 heures</SelectItem>
                    <SelectItem value="24h">24 heures</SelectItem>
                    <SelectItem value="3d">3 jours</SelectItem>
                    <SelectItem value="7d">7 jours</SelectItem>
                    <SelectItem value="30d">30 jours</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}
            <div>
              <label className="text-sm font-medium mb-1 block">Raison</label>
              <Textarea
                value={sanctionForm.reason}
                onChange={(e) => setSanctionForm({...sanctionForm, reason: e.target.value})}
                placeholder="Raison de la sanction..."
                className="min-h-[80px]"
                data-testid="sanction-reason"
              />
            </div>
            <Button onClick={handleCreateSanction} className="w-full" data-testid="submit-sanction-button">
              Appliquer la sanction
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
